import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { differenceInCalendarDays } from 'date-fns'
import { InsightTimeType, useInsightsTime, useStore } from 'zens-store'
import {
  CalendarValue,
  ChartBar,
  Color,
  Hyperlink,
  Radius,
  Spacing,
  Text,
  dateFormatter,
  shortLocalizedTime,
  calendarValueToInsightsFromTo,
  Weekday,
} from 'zens-storybook'
import { Link } from 'zens-router'

import { groupByDay, groupByWeek } from '@dms/helper/insights'

import { InfoBlock } from '../info'
import { TimeZoneConflict } from '../location'

import InsightsTimeSelect from './timeselect'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.M,
  },
  chart: {
    minHeight: 250,
  },
  title: {
    flex: 1,
  },
  more: {
    marginTop: Spacing.M,
    alignSelf: 'flex-end',
  },
  skeletonTitle: {
    height: 32,
    justifyContent: 'center',
  },
  skeletonChart: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
    marginBottom: 46,
  },
})

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
  type?: InsightTimeType
  onChangeType?: (type: InsightTimeType) => void
}

const InsightsOverviewGraph = (props: Props) => {
  const {
    companyId,
    buildingId,
    floorId,
    period,
    weekdays,
    workingHours,
    type = 'day',
    onChangeType,
    style,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const { i18n } = useStore()

  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const rangeInDays = dates ? differenceInCalendarDays(dates.to!, dates.from!) + 1 : 0
  const hourlyDisabled = rangeInDays > 31
  const enabled = !!dates && rangeInDays <= 366
  const effectiveType: InsightTimeType = hourlyDisabled ? 'day' : type

  useEffect(() => {
    if (hourlyDisabled && type !== 'day') {
      onChangeType?.('day')
    }
  }, [hourlyDisabled, onChangeType, type])

  const insights = useInsightsTime(
    {
      companyId,
      buildingId,
      floorId,
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
      timeType: effectiveType,
    },
    {
      enabled: enabled && !!companyId && (!!buildingId || !!floorId),
    },
  )

  const items =
    effectiveType === 'day'
      ? !weekdays || weekdays.length > 1
        ? groupByWeek(insights.data)
        : insights.data
      : groupByDay(insights.data)

  const data = {
    values: items?.map((item) => item?.value) || [],
    renderLabel: (value: number) => `${value}%`,
  }

  const horizontalAxis = {
    renderLabel: (index: number) => {
      const item = items?.[index]
      if (!item || item.value === null) return ''
      const { date } = item
      if (effectiveType === 'hour') return shortLocalizedTime(date, i18n.language)!
      return dateFormatter(date, 'd LLL', i18n.language)!
    },
  }

  const verticalAxis = {
    max: 100,
    title: `${t('Average occupancy')} (%)`,
    renderLabel: (value: number) => `${value}`,
  }

  return (
    <InfoBlock
      title={{
        text: (
          <Text numberOfLines={1} level={4} style={styles.title}>
            {t('Average occupancy over time')}
          </Text>
        ),
        right: <InsightsTimeSelect value={effectiveType} onChange={onChangeType} period={period} />,
      }}
      style={[styles.container, style]}
      {...otherProps}
    >
      <ChartBar data={[data]} horizontalAxis={horizontalAxis} verticalAxis={verticalAxis} style={styles.chart} />
      <Link href={`/companies/${companyId}/insights/${buildingId}/time`} asChild>
        <Hyperlink title={t('View more')} right="right" iconSize={14} style={styles.more} testID="OverviewGraph.More" />
      </Link>
      <TimeZoneConflict companyId={companyId} buildingId={buildingId} />
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()

  return (
    <InfoBlock.Skeleton
      style={[styles.container, props.style]}
      title={
        <View style={styles.skeletonTitle}>
          <Text level={4}>{t('Average occupancy over time')}</Text>
        </View>
      }
    >
      <View style={[styles.chart, styles.skeletonChart]} />
    </InfoBlock.Skeleton>
  )
}
InsightsOverviewGraph.Skeleton = Skeleton

export default InsightsOverviewGraph
